import React, { useState } from 'react';
import api from '../../api/client';
import { Upload, Download, X, CheckCircle2, AlertCircle, Loader2, FileSpreadsheet } from 'lucide-react';

const ENTITY_LABEL = {
  sites: 'Emplazamientos',
  normativa: 'Normativa',
  incidents: 'Incidencias',
  preventive: 'Preventivo',
  conflictive: 'Conflictivos',
  agenda: 'Agenda',
  towers: 'Torres',
  nodes: 'Nodos activos',
};

const CsvImport = ({ entity, onImported, buttonLabel = 'Importar CSV', compact = false }) => {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [replace, setReplace] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const download = async () => {
    try {
      const res = await api.get(`/import/${entity}/template`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `template_${entity}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert('Error descargando plantilla: ' + (e.response?.data?.detail || e.message));
    }
  };

  const upload = async () => {
    if (!file) return;
    setLoading(true); setResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post(`/import/${entity}?replace=${replace}`, fd);
      setResult(data);
      onImported?.(data);
    } catch (e) {
      setResult({ error: e.response?.data?.detail || e.message });
    } finally { setLoading(false); }
  };

  const close = () => { setOpen(false); setFile(null); setResult(null); setReplace(false); };

  const btnCls = compact
    ? 'flex items-center gap-1.5 border border-white/10 hover:border-[#A3FF12]/40 text-white/70 hover:text-[#A3FF12] px-3 py-2 rounded-xl text-xs font-bold'
    : 'flex items-center gap-2 border border-[#A3FF12]/40 text-[#A3FF12] hover:bg-[#A3FF12]/10 px-3 py-2 rounded-xl text-sm font-bold';

  return (
    <>
      <button onClick={() => setOpen(true)} className={btnCls} data-testid={`btn-csv-${entity}`}>
        <FileSpreadsheet size={compact ? 12 : 14}/> {buttonLabel}
      </button>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={close}>
          <div onClick={(e) => e.stopPropagation()} data-testid={`csv-modal-${entity}`}
            className="bg-[#0a0a0a] border border-white/10 rounded-t-2xl sm:rounded-2xl w-full max-w-lg">
            <div className="p-5 border-b border-white/10 flex justify-between items-start">
              <div>
                <div className="text-[10px] font-black tracking-widest text-[#A3FF12]">IMPORTAR CSV / EXCEL</div>
                <h3 className="text-lg font-black mt-1">{ENTITY_LABEL[entity] || entity}</h3>
                <p className="text-white/50 text-xs mt-1">Sube CSV o XLSX con las columnas de la plantilla. CSV: delimitador auto-detectado (,;|tab).</p>
              </div>
              <button onClick={close} className="text-white/40 hover:text-white"><X size={22}/></button>
            </div>

            <div className="p-5 space-y-3">
              <button onClick={download} data-testid={`btn-template-${entity}`}
                className="w-full flex items-center justify-center gap-2 border border-white/10 hover:border-[#A3FF12]/30 text-white/70 hover:text-[#A3FF12] rounded-xl py-2.5 text-sm font-bold">
                <Download size={14}/> Descargar plantilla CSV
              </button>

              <label className="block border-2 border-dashed border-white/10 hover:border-[#A3FF12]/40 rounded-xl p-6 text-center cursor-pointer transition-colors">
                <Upload className="mx-auto text-white/40 mb-2" size={28}/>
                <div className="text-white/70 text-sm font-bold">
                  {file ? file.name : 'Elige un archivo CSV o Excel'}
                </div>
                <div className="text-white/40 text-xs mt-1">{file ? `${(file.size / 1024).toFixed(1)} KB` : 'Acepta .csv y .xlsx · máx recomendado 10.000 filas'}</div>
                <input type="file" accept=".csv,text/csv,.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" className="hidden" data-testid={`csv-file-${entity}`}
                  onChange={(e) => { setFile(e.target.files[0]); setResult(null); }}/>
              </label>

              <label className="flex items-center gap-2 text-sm text-white/70 cursor-pointer">
                <input type="checkbox" checked={replace} onChange={(e) => setReplace(e.target.checked)}
                  className="rounded border-white/20 bg-black/40" data-testid={`csv-replace-${entity}`}/>
                <span>Reemplazar toda la colección (⚠️ borra existente)</span>
              </label>

              {result && !result.error && (
                <div className="bg-[#A3FF12]/10 border border-[#A3FF12]/30 rounded-xl p-3 text-sm">
                  <div className="flex items-center gap-2 font-bold text-[#A3FF12]">
                    <CheckCircle2 size={16}/> Importadas {result.inserted} filas
                  </div>
                  <div className="text-white/60 text-xs mt-1">
                    Delimitador: <code>{result.delimiter_used}</code>
                    {result.errors_count > 0 && <> · Errores: <span className="text-orange-400">{result.errors_count}</span></>}
                  </div>
                  {result.errors_sample?.length > 0 && (
                    <details className="mt-2 text-xs">
                      <summary className="cursor-pointer text-orange-300">Ver {result.errors_sample.length} errores</summary>
                      <ul className="mt-1 space-y-1 max-h-32 overflow-y-auto">
                        {result.errors_sample.map((e, i) => (
                          <li key={i} className="text-red-300 font-mono">Fila {e.row}: {e.error}</li>
                        ))}
                      </ul>
                    </details>
                  )}
                </div>
              )}

              {result?.error && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-3 text-sm text-red-300 flex items-center gap-2">
                  <AlertCircle size={16}/> {result.error}
                </div>
              )}
            </div>

            <div className="p-5 border-t border-white/10 flex justify-end gap-2">
              <button onClick={close} className="px-4 py-2 border border-white/10 rounded-xl text-sm">Cerrar</button>
              <button onClick={upload} disabled={!file || loading} data-testid={`btn-do-import-${entity}`}
                className="bg-[#A3FF12] text-black font-black px-4 py-2 rounded-xl disabled:opacity-50 flex items-center gap-2 text-sm">
                {loading ? <Loader2 className="animate-spin" size={14}/> : <Upload size={14}/>}
                {loading ? 'Importando…' : 'Importar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CsvImport;
